import { useMemo } from 'react';

import { useController, Control, FieldValues, FieldPath } from 'react-hook-form';
import { Select, Spinner } from '@schibsted-svp/react-ui';
import type { ExtendedProps as SelectProps, DefaultOptionType } from '@schibsted-svp/react-ui/lib/es/Select';

import { useListTagsQuery } from '../../../services/admin-bff-sdk';
import { useTagsSearch } from '../../AssetForm/hooks/useTagsSearch';
import { findMultipleOptions, getValuesFromOptionsArray } from '../../ui/Form/Select';

import css from './TagsSelect.module.scss';

function LoadingMessage() {
    return <Spinner containerClassName={css.optionsSpinner} size="compact" label="Loading cast members" />;
}
function LoadingIndicator() {
    return null;
}

type CastMembersSelectProps<TFieldValues extends FieldValues> = {
    name: FieldPath<TFieldValues>;
    control: Control<TFieldValues>;
    provider: string;
} & SelectProps<DefaultOptionType, true>;

export function CastMembersSelect<TFieldValues extends FieldValues>({
    name,
    control,
    provider,
}: CastMembersSelectProps<TFieldValues>) {
    const { field, fieldState } = useController({ name, control });
    const {
        data,
        isFetching: isListTagsFetching,
        isLoading: isListTagsLoading,
    } = useListTagsQuery({ provider, ids: field.value }, { skip: !field.value?.length });
    const { isFetching, isLoading, onTagsSearch, searchResults } = useTagsSearch({ provider, delay: 200 });

    const selectedOptions = useMemo(() => {
        return data?.listTags.map((tag) => ({ value: tag.id, label: tag.title })) || [];
    }, [data]);

    const options =
        searchResults?.filter(({ type }) => type === 'person').map(({ id, title }) => ({ value: id, label: title })) ??
        [];

    const formatOptionLabel = ({ label }: DefaultOptionType) => {
        if (isListTagsLoading || !label) {
            return <Spinner containerClassName={css.selectSpinner} />;
        }

        return label;
    };

    return (
        <Select
            value={findMultipleOptions(selectedOptions)(field.value)}
            onChange={(newValue) => field.onChange(getValuesFromOptionsArray(newValue))}
            error={fieldState.error?.message}
            placeholder="Search for cast members"
            isLoading={isListTagsFetching || isListTagsLoading || isFetching || isLoading}
            onInputChange={onTagsSearch}
            options={options}
            formatOptionLabel={formatOptionLabel}
            components={{ LoadingMessage, LoadingIndicator }}
            backspaceRemovesValue={false}
            splitButton={false}
            isMulti
            isClearable
            size="small"
        />
    );
}
